<template>
  <div class="sticky-header-container">
    <!-- ================= 1. DESKTOP HEADER (width >= 992px) ================= -->
    <nav class="navbar navbar-expand-lg navbar-light custom-navbar d-none d-lg-block">
      <div class="container-fluid py-1 px-3">
        <!-- Brand Logo -->
        <router-link to="/" class="navbar-brand d-flex align-items-center gap-2">
          <div class="brand-icon-wrapper">
            <img src="@/assets/img/loidoc_logo_custom.png" alt="Lối Đọc" class="brand-logo-img" />
          </div>
          <div class="brand-text">
            <span class="brand-title font-serif">Lối Đọc</span>
            <small class="brand-subtitle text-muted">Nuôi dưỡng tâm hồn</small>
          </div>
        </router-link>

        <!-- Collapsible Desktop links -->
        <div class="collapse navbar-collapse show">
          <!-- Main Links -->
          <ul class="navbar-nav mb-0 gap-2 ps-lg-4">
            <li class="nav-item">
              <router-link to="/" class="nav-link-custom" active-class="active" title="Trang chủ">
                <icon icon="fa-solid fa-house" class="nav-link-icon" />
                <span>Trang chủ</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/smart-recommendation" class="nav-link-custom" active-class="active" title="Đề xuất thông minh">
                <icon icon="fa-solid fa-wand-magic-sparkles" class="nav-link-icon me-1" />
                <span>Đề xuất thông minh</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/my-bookcase" class="nav-link-custom" active-class="active" title="Tủ sách của tôi" @click="clearBookcaseState">
                <icon icon="fa-solid fa-book-bookmark" class="nav-link-icon me-1" />
                <span>Tủ sách của tôi</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/book-suggest" class="nav-link-custom" active-class="active" title="Đề xuất sách">
                <icon icon="fa-solid fa-paper-plane" class="nav-link-icon me-1" />
                <span>Đề xuất sách</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/old-books" class="nav-link-custom" active-class="active" title="Sách cũ">
                <icon icon="fa-solid fa-store" class="nav-link-icon me-1" />
                <span>Sách cũ</span>
              </router-link>
            </li>
          </ul>

          <!-- Search bar in header -->
          <div class="header-search-wrapper mx-auto">
            <form @submit.prevent="handleHeaderSearch" class="header-search-form position-relative" @click="focusSearch('desktop')">
              <icon icon="fa-solid fa-magnifying-glass" class="header-search-icon text-muted" />
              <input
                ref="desktopSearchInput"
                v-model.trim="headerSearchKey"
                type="text"
                class="header-search-input"
                placeholder="Tìm kiếm sách..."
                @focus="openSuggestions"
                @click.stop="openSuggestions"
                @input="openSuggestions"
              />
              <button v-if="headerSearchKey" type="button" @mousedown.prevent @click.stop="clearHeaderSearch" class="btn-clear-header-search">
                <icon icon="fa-solid fa-xmark" />
              </button>

              <!-- Suggestions Dropdown (Desktop Header) -->
              <div v-if="showSuggestions" class="search-suggestions-dropdown text-start" @click.stop>
                  
                  <!-- Recent Searches -->
                  <div v-if="!headerSearchKey" class="suggestion-section">
                    <div class="section-title d-flex justify-content-between align-items-center px-3 py-2">
                      <span><icon icon="fa-solid fa-clock-rotate-left" class="me-2 text-muted" />TÌM KIẾM GẦN ĐÂY</span>
                      <button v-if="filteredRecentSearches.length > 0" type="button" class="btn-clear-all fs-8 text-danger border-0 bg-transparent p-0" @mousedown.prevent @click.stop="clearAllRecent">Xóa tất cả</button>
                    </div>
                    <div v-if="filteredRecentSearches.length > 0" class="suggestion-list">
                      <div
                        v-for="(item, idx) in filteredRecentSearches"
                        :key="'recent-hdr-' + idx"
                        class="suggestion-item d-flex align-items-center justify-content-between px-3 py-2"
                        @mousedown.prevent
                        @click="selectHeaderSuggestion(item)"
                      >
                        <span class="suggestion-text"><icon icon="fa-solid fa-clock" class="me-2 text-muted-dark fs-8" />{{ item }}</span>
                        <button type="button" class="btn-delete-item border-0 bg-transparent text-muted p-1" @mousedown.prevent @click.stop="removeRecentSearch(item)" title="Xóa lịch sử">
                          <icon icon="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>
                    <div v-else-if="!headerSearchKey" class="px-3 py-2 text-white-50 fs-8">
                      Chưa có lịch sử tìm kiếm
                    </div>
                  </div>

                  <!-- Quick Book Matches -->
                  <div v-if="headerSearchKey" class="suggestion-section border-top">
                    <div class="section-title px-3 py-2">
                      <span><icon icon="fa-solid fa-book" class="me-2 text-primary" />SÁCH GỢI Ý NHANH</span>
                    </div>
                    <div v-if="isSuggestLoading" class="px-3 py-2 text-muted fs-8 d-flex align-items-center">
                      <span class="spinner-border spinner-border-sm me-2 text-light" role="status" aria-hidden="true"></span>
                      <span class="text-white-50">Đang tìm kiếm...</span>
                    </div>
                    <div v-else-if="filteredBooks.length > 0" class="suggestion-list">
                      <div
                        v-for="book in filteredBooks.slice(0, 3)"
                        :key="'hdr-book-' + book.id"
                        class="suggestion-item book-item-preview d-flex align-items-center gap-3 px-3 py-2"
                        @mousedown.prevent
                        @click="selectBookSuggestion(book)"
                      >
                        <img v-if="book.cover" :src="book.cover" class="book-preview-cover img-fluid" style="width: 24px; height: 36px; object-fit: cover; border-radius: 4px;" @error="handleCoverError(book)" />
                        <div v-else class="book-preview-cover fallback-cover-mini d-flex align-items-center justify-content-center" style="width: 24px; height: 36px; background: #2d5d5f; border-radius: 4px;">
                          <icon icon="fa-solid fa-book" class="text-white-50" style="font-size: 0.6rem;" />
                        </div>
                        <div class="book-preview-info">
                          <h5 class="book-preview-title">{{ book.name }}</h5>
                          <div class="book-meta-details">
                            <div class="meta-line"><span class="meta-label">Tác giả:</span> <span class="meta-value">{{ getAuthorsString(book) }}</span></div>
                            <div v-if="getTranslatorsString(book)" class="meta-line"><span class="meta-label">Dịch giả:</span> <span class="meta-value">{{ getTranslatorsString(book) }}</span></div>
                            <div v-if="getCategoriesString(book)" class="meta-line"><span class="meta-label">Thể loại:</span> <span class="meta-value">{{ getCategoriesString(book) }}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="px-3 py-2 text-white-50 fs-8">
                      Không tìm thấy sách phù hợp
                    </div>
                  </div>

                </div>
            </form>
          </div>

          <!-- User Section -->
          <div class="navbar-user-section d-flex align-items-center gap-2">
            <!-- Old Book Cart Button (Desktop) -->
            <button 
              type="button" 
              class="header-cart-btn" 
              @click="toggleDrawer" 
              title="Giỏ hàng sách cũ"
            >
              <icon icon="fa-solid fa-cart-shopping" />
              <span v-if="totalItems > 0" class="header-cart-badge">{{ totalItems > 99 ? '99+' : totalItems }}</span>
            </button>

            <!-- Notification Bell (Desktop) -->
            <NotificationBell v-if="isLoggedIn" />

            <div v-if="isLoggedIn" class="dropdown">
              <button 
                class="btn-profile-dropdown dropdown-toggle d-flex align-items-center gap-2 p-2 rounded-pill"
                type="button" 
                id="userDropdown"
                ref="userDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
                @click="toggleUserDropdown"
              >
                <div class="avatar-circle">
                  <img v-if="userAvatar" :src="userAvatar" alt="Avatar" class="avatar-img" referrerpolicy="no-referrer" />
                  <icon v-else icon="fa-solid fa-user" />
                </div>
                <span class="user-name-label fw-bold text-primary-color">{{ displayName }}</span>
                <icon icon="fa-solid fa-caret-down" class="dropdown-chevron-icon text-muted" />
              </button>
              
              <ul class="dropdown-menu dropdown-menu-end shadow-md mt-2 border-0 rounded-4 py-2" aria-labelledby="userDropdown">
                <li>
                  <div class="dropdown-header text-start px-4 pt-1 pb-2">
                    <span class="d-block text-xs text-muted mb-1">Đang đăng nhập:</span>
                    <strong class="text-primary-color fs-7 text-truncate d-block" style="max-width: 180px;">{{ displayName }}</strong>
                  </div>
                </li>
                <li><hr class="dropdown-divider my-1 mx-3 border-secondary-subtle"></li>
                <li>
                  <router-link to="/settings/profile" class="dropdown-item py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                    <span class="d-flex justify-content-center align-items-center text-muted" style="width: 20px;">
                      <icon icon="fa-solid fa-user-gear" />
                    </span>
                    <span>Cập nhật hồ sơ</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/settings/preferences" class="dropdown-item py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                    <span class="d-flex justify-content-center align-items-center text-warning-gold" style="width: 20px;">
                      <icon icon="fa-solid fa-wand-magic-sparkles" />
                    </span>
                    <span>Cá nhân hóa tìm kiếm</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/my-old-books" class="dropdown-item py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                    <span class="d-flex justify-content-center align-items-center text-primary" style="width: 20px;">
                      <icon icon="fa-solid fa-boxes-packing" />
                    </span>
                    <span>Quản lý sách cũ của tôi</span>
                  </router-link>
                </li>
                <li><hr class="dropdown-divider my-1 mx-3 border-secondary-subtle"></li>
                <li>
                  <button @click="handleLogout" class="dropdown-item text-danger py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                    <span class="d-flex justify-content-center align-items-center text-danger" style="width: 20px;">
                      <icon icon="fa-solid fa-right-from-bracket" />
                    </span>
                    <span>Đăng xuất</span>
                  </button>
                </li>
              </ul>
            </div>
            <div v-else>
              <router-link to="/login" class="btn-login-custom">
                <icon icon="fa-solid fa-right-to-bracket" class="login-btn-icon" />
                <span>Đăng nhập</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- ================= 2. MOBILE & TABLET TOP HEADER BAR (width < 992px) ================= -->
    <header class="mobile-top-header d-lg-none">
      <div class="container-fluid d-flex align-items-center justify-content-between h-100 px-3">
        <!-- Brand logo icon on Mobile, Page Title on iPad -->
        <div class="d-flex align-items-center gap-2 fade-in-down-fast">
          <router-link to="/" class="mobile-brand-link d-flex align-items-center text-decoration-none d-sm-none">
            <img src="@/assets/img/loidoc_logo_custom.png" alt="Lối Đọc" class="mobile-brand-logo-img" />
          </router-link>
          <span class="mobile-page-title font-serif d-none d-sm-inline">{{ pageTitle }}</span>
        </div>
        
        <!-- Search Bar: Always visible on Tablet and Mobile -->
        <div class="mobile-header-search flex-grow-1 mx-2">
          <form @submit.prevent="handleHeaderSearch" class="header-search-form position-relative" @click="focusSearch('mobile')">
            <icon icon="fa-solid fa-magnifying-glass" class="header-search-icon text-muted" />
            <input
              ref="mobileSearchInput"
              v-model.trim="headerSearchKey"
              type="text"
              class="header-search-input"
              placeholder="Tìm kiếm..."
              @focus="openSuggestions"
              @click.stop="openSuggestions"
              @input="openSuggestions"
            />
            <button v-if="headerSearchKey" type="button" @mousedown.prevent @click.stop="clearHeaderSearch" class="btn-clear-header-search">
              <icon icon="fa-solid fa-xmark" />
            </button>

            <!-- Suggestions Dropdown (Mobile/Tablet Header) -->
            <div v-if="showSuggestions" class="search-suggestions-dropdown text-start" @click.stop>
                
                <!-- Recent Searches -->
                <div v-if="!headerSearchKey" class="suggestion-section">
                  <div class="section-title d-flex justify-content-between align-items-center px-3 py-2">
                    <span><icon icon="fa-solid fa-clock-rotate-left" class="me-2 text-muted" />TÌM KIẾM GẦN ĐÂY</span>
                    <button v-if="filteredRecentSearches.length > 0" type="button" class="btn-clear-all fs-8 text-danger border-0 bg-transparent p-0" @mousedown.prevent @click.stop="clearAllRecent">Xóa tất cả</button>
                  </div>
                  <div v-if="filteredRecentSearches.length > 0" class="suggestion-list">
                    <div
                      v-for="(item, idx) in filteredRecentSearches"
                      :key="'recent-hdr-mob-' + idx"
                      class="suggestion-item d-flex align-items-center justify-content-between px-3 py-2"
                      @mousedown.prevent
                      @click="selectHeaderSuggestion(item)"
                    >
                      <span class="suggestion-text"><icon icon="fa-solid fa-clock" class="me-2 text-muted-dark fs-8" />{{ item }}</span>
                      <button type="button" class="btn-delete-item border-0 bg-transparent text-muted p-1" @mousedown.prevent @click.stop="removeRecentSearch(item)" title="Xóa lịch sử">
                        <icon icon="fa-solid fa-xmark" />
                      </button>
                    </div>
                  </div>
                  <div v-else-if="!headerSearchKey" class="px-3 py-2 text-white-50 fs-8">
                    Chưa có lịch sử tìm kiếm
                  </div>
                </div>

                <!-- Quick Book Matches -->
                <div v-if="headerSearchKey" class="suggestion-section border-top">
                  <div class="section-title px-3 py-2">
                    <span><icon icon="fa-solid fa-book" class="me-2 text-primary" />SÁCH GỢI Ý NHANH</span>
                  </div>
                  <div v-if="isSuggestLoading" class="px-3 py-2 text-muted fs-8 d-flex align-items-center">
                    <span class="spinner-border spinner-border-sm me-2 text-light" role="status" aria-hidden="true"></span>
                    <span class="text-white-50">Đang tìm kiếm...</span>
                  </div>
                  <div v-else-if="filteredBooks.length > 0" class="suggestion-list">
                    <div
                      v-for="book in filteredBooks.slice(0, 3)"
                      :key="'hdr-mob-book-' + book.id"
                      class="suggestion-item book-item-preview d-flex align-items-center gap-3 px-3 py-2"
                      @mousedown.prevent
                      @click="selectBookSuggestion(book)"
                    >
                      <img v-if="book.cover" :src="book.cover" class="book-preview-cover img-fluid" style="width: 24px; height: 36px; object-fit: cover; border-radius: 4px;" @error="handleCoverError(book)" />
                      <div v-else class="book-preview-cover fallback-cover-mini d-flex align-items-center justify-content-center" style="width: 24px; height: 36px; background: #2d5d5f; border-radius: 4px;">
                        <icon icon="fa-solid fa-book" class="text-white-50" style="font-size: 0.6rem;" />
                      </div>
                      <div class="book-preview-info">
                        <h5 class="book-preview-title">{{ book.name }}</h5>
                        <div class="book-meta-details">
                          <div class="meta-line"><span class="meta-label">Tác giả:</span> <span class="meta-value">{{ getAuthorsString(book) }}</span></div>
                          <div v-if="getTranslatorsString(book)" class="meta-line"><span class="meta-label">Dịch giả:</span> <span class="meta-value">{{ getTranslatorsString(book) }}</span></div>
                          <div v-if="getCategoriesString(book)" class="meta-line"><span class="meta-label">Thể loại:</span> <span class="meta-value">{{ getCategoriesString(book) }}</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="px-3 py-2 text-white-50 fs-8">
                    Không tìm thấy sách phù hợp
                  </div>
                </div>

              </div>
          </form>
        </div>
        
        <!-- Header user status avatar dropdown or login link -->
        <div class="d-flex align-items-center gap-2">
          <!-- Old Book Cart Button (Mobile) -->
          <button 
            type="button" 
            class="header-cart-btn mobile-cart-btn" 
            @click="toggleDrawer" 
            title="Giỏ hàng sách cũ"
          >
            <icon icon="fa-solid fa-cart-shopping" />
            <span v-if="totalItems > 0" class="header-cart-badge">{{ totalItems > 99 ? '99+' : totalItems }}</span>
          </button>

          <!-- Notification Bell (Mobile) -->
          <NotificationBell v-if="isLoggedIn" />

          <div v-if="isLoggedIn" class="mobile-header-user dropdown">
          <button
            class="mobile-avatar-btn dropdown-toggle"
            type="button"
            id="mobileUserDropdown"
            ref="mobileUserDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            :title="displayName"
            @click="toggleUserDropdown"
          >
            <div class="mobile-avatar-circle">
              <img v-if="userAvatar" :src="userAvatar" alt="Avatar" class="avatar-img" referrerpolicy="no-referrer" />
              <template v-else>{{ displayName.charAt(0).toUpperCase() }}</template>
            </div>
          </button>
          
          <ul class="dropdown-menu dropdown-menu-end shadow-md mt-2 border-0 rounded-4 py-2" aria-labelledby="mobileUserDropdown">
            <li>
              <div class="dropdown-header text-start px-4 pt-1 pb-2">
                <span class="d-block text-xs text-muted mb-1">Đang đăng nhập:</span>
                <strong class="text-primary-color fs-7-5 text-truncate d-block" style="max-width: 180px;">{{ displayName }}</strong>
              </div>
            </li>
            <li><hr class="dropdown-divider my-1 mx-3 border-secondary-subtle"></li>
            <li>
              <router-link to="/settings/profile" class="dropdown-item py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                <span class="d-flex justify-content-center align-items-center text-muted" style="width: 20px;">
                  <icon icon="fa-solid fa-user-gear" />
                </span>
                <span>Cập nhật hồ sơ</span>
              </router-link>
            </li>
            <li>
              <router-link to="/settings/preferences" class="dropdown-item py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                <span class="d-flex justify-content-center align-items-center text-warning-gold" style="width: 20px;">
                  <icon icon="fa-solid fa-wand-magic-sparkles" />
                </span>
                <span>Cá nhân hóa tìm kiếm</span>
              </router-link>
            </li>
            <li>
              <router-link to="/my-old-books" class="dropdown-item py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                <span class="d-flex justify-content-center align-items-center text-primary" style="width: 20px;">
                  <icon icon="fa-solid fa-boxes-packing" />
                </span>
                <span>Quản lý sách cũ của tôi</span>
              </router-link>
            </li>
            <li><hr class="dropdown-divider my-1 mx-3 border-secondary-subtle"></li>
            <li>
              <button @click="handleLogout" class="dropdown-item text-danger py-2 px-4 d-flex align-items-center gap-2 fs-7-5">
                <span class="d-flex justify-content-center align-items-center text-danger" style="width: 20px;">
                  <icon icon="fa-solid fa-right-from-bracket" />
                </span>
                <span>Đăng xuất</span>
              </button>
            </li>
          </ul>
          </div><!-- end mobile-header-user dropdown -->
          <div v-if="!isLoggedIn" class="mobile-header-user">
            <router-link to="/login" class="btn-login-custom-mobile">
              <span>Đăng nhập</span>
            </router-link>
          </div>
        </div><!-- end d-flex gap-2 -->
      </div>
    </header>

    <!-- ================= 3. MOBILE APP BOTTOM NAVIGATION BAR (width < 576px) ================= -->
    <div class="bottom-tab-bar d-flex d-sm-none">
      <router-link to="/" class="mobile-tab-item" exact-active-class="active">
        <icon icon="fa-solid fa-house" />
        <span>Trang chủ</span>
      </router-link>
      <router-link to="/my-bookcase" class="mobile-tab-item" active-class="active" @click="clearBookcaseState">
        <icon icon="fa-solid fa-book-bookmark" />
        <span>Tủ sách</span>
      </router-link>
      <router-link to="/book-suggest" class="mobile-tab-item" active-class="active">
        <icon icon="fa-solid fa-paper-plane" />
        <span>Đề xuất</span>
      </router-link>
      <router-link to="/smart-recommendation" class="mobile-tab-item" active-class="active">
        <icon icon="fa-solid fa-wand-magic-sparkles" />
        <span>AI Đề xuất</span>
      </router-link>
      <router-link to="/old-books" class="mobile-tab-item" active-class="active">
        <icon icon="fa-solid fa-store" />
        <span>Sách cũ</span>
      </router-link>
      <router-link to="/info" class="mobile-tab-item" active-class="active">
        <icon icon="fa-solid fa-circle-info" />
        <span>Thông tin</span>
      </router-link>
    </div>

    <!-- ================= 4. IPAD LEFT-SIDE NAVIGATION RAIL (576px <= width < 992px) ================= -->
    <div class="ipad-nav-rail d-none d-sm-flex d-lg-none">
      <div class="d-flex flex-column align-items-center w-100">
        <!-- Top App icon -->
        <router-link to="/" class="rail-logo-wrapper text-decoration-none">
          <img src="@/assets/img/loidoc_logo_custom.png" alt="Lối Đọc" class="rail-logo-img" />
        </router-link>
        
        <!-- Navigation tab items -->
        <router-link to="/" class="ipad-rail-item" exact-active-class="active">
          <icon icon="fa-solid fa-house" />
          <span>Trang chủ</span>
        </router-link>
        <router-link to="/my-bookcase" class="ipad-rail-item" active-class="active" @click="clearBookcaseState">
          <icon icon="fa-solid fa-book-bookmark" />
          <span>Tủ sách</span>
        </router-link>
        <router-link to="/book-suggest" class="ipad-rail-item" active-class="active">
          <icon icon="fa-solid fa-paper-plane" />
          <span>Đề xuất</span>
        </router-link>
        <router-link to="/smart-recommendation" class="ipad-rail-item" active-class="active">
          <icon icon="fa-solid fa-wand-magic-sparkles" />
          <span>AI Đề xuất</span>
        </router-link>
        <router-link to="/old-books" class="ipad-rail-item" active-class="active">
          <icon icon="fa-solid fa-store" />
          <span>Sách cũ</span>
        </router-link>
        <router-link to="/info" class="ipad-rail-item" active-class="active">
          <icon icon="fa-solid fa-circle-info" />
          <span>Thông tin</span>
        </router-link>
      </div>
    </div>

    <!-- Global Old Book Cart Drawer -->
    <OldBookCartDrawer />
  </div>
</template>
<script src="./Header.js"></script>
<style scoped src="./Header.css"></style>
<style>
/* Global Responsive Adjustments for body element when layout rail/bars are present */
@media (max-width: 575.98px) {
  body.has-ipad-rail {
    padding-bottom: 64px !important; /* Prevent bottom tab bar overlap on mobile */
  }
}

@media (min-width: 576px) and (max-width: 991.98px) {
  body.has-ipad-rail {
    padding-left: 72px !important; /* Prevent left navigation rail overlap on iPad */
  }
}
</style>
